import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { IconButton, Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import {
  ResumeColorMode,
  ResumeFontFamilyId,
  ResumePersonalization,
} from '../../../types';
import { Palette, radius, spacing, useThemePalette } from '../../../theme';
import {
  FONT_SCALE_MAX,
  FONT_SCALE_MIN,
  FONT_SCALE_STEP,
  fontFamilyIds,
} from '../../../lib/resume';
import { FONT_FAMILIES } from '../../../lib/resume/helpers';

interface Props {
  value: ResumePersonalization;
  onChange: (next: ResumePersonalization) => void;
}

const MODES: { id: ResumeColorMode; icon: 'weather-sunny' | 'weather-night' }[] = [
  { id: 'light', icon: 'weather-sunny' },
  { id: 'dark', icon: 'weather-night' },
];

// On-screen preview for each font option. Native Paper Text on RN will pick
// the closest system font that matches these stacks.
const FONT_PREVIEW: Record<ResumeFontFamilyId, string> = {
  sans: 'System',
  serif: 'Georgia',
  modern: 'Avenir-Book',
  mono: 'Menlo',
};

// Snap to the nearest 10% step and clamp to the supported range.
const clampScale = (v: number) =>
  Math.max(FONT_SCALE_MIN, Math.min(FONT_SCALE_MAX, Math.round(v * 10) / 10));

type SizeKey = 'title' | 'subtitle' | 'body';

const PersonalizationForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const set = <K extends keyof ResumePersonalization>(
    key: K,
    next: ResumePersonalization[K]
  ) => onChange({ ...value, [key]: next });

  const setScale = (key: SizeKey, next: number) =>
    onChange({
      ...value,
      fontScale: { ...value.fontScale, [key]: clampScale(next) },
    });

  const renderStepper = (key: SizeKey, label: string) => {
    const v = value.fontScale[key];
    const canDec = v > FONT_SCALE_MIN + 0.001;
    const canInc = v < FONT_SCALE_MAX - 0.001;
    return (
      <View style={styles.stepperRow}>
        <Text style={styles.stepperLabel}>{label}</Text>
        <View style={styles.stepperControls}>
          <IconButton
            icon="minus"
            mode="outlined"
            onPress={() => setScale(key, v - FONT_SCALE_STEP)}
            disabled={!canDec}
            size={18}
            accessibilityLabel={t('resume.personalization.decrease', {
              field: label,
            })}
          />
          <Text style={styles.stepperValue}>{Math.round(v * 100)}%</Text>
          <IconButton
            icon="plus"
            mode="outlined"
            onPress={() => setScale(key, v + FONT_SCALE_STEP)}
            disabled={!canInc}
            size={18}
            accessibilityLabel={t('resume.personalization.increase', {
              field: label,
            })}
          />
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <Text style={styles.sectionTitle}>
        {t('resume.personalization.colorMode')}
      </Text>
      <View style={styles.modeRow}>
        {MODES.map((m) => {
          const selected = value.colorMode === m.id;
          return (
            <TouchableOpacity
              key={m.id}
              onPress={() => set('colorMode', m.id)}
              activeOpacity={0.85}
              style={[styles.modeChip, selected && styles.modeChipSelected]}
            >
              <MaterialCommunityIcons
                name={m.icon}
                size={18}
                color={selected ? palette.appliedText : palette.textPrimary}
              />
              <Text
                style={[
                  styles.modeChipText,
                  selected && styles.modeChipTextSelected,
                ]}
              >
                {t(`resume.personalization.modes.${m.id}`)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <Text style={[styles.sectionTitle, styles.sectionGap]}>
        {t('resume.personalization.fontFamily')}
      </Text>
      <View style={styles.fontList}>
        {fontFamilyIds.map((id) => {
          const selected = value.fontFamily === id;
          return (
            <TouchableOpacity
              key={id}
              onPress={() => set('fontFamily', id)}
              activeOpacity={0.85}
              style={[styles.fontRow, selected && styles.fontRowSelected]}
            >
              <View style={styles.fontInfo}>
                <Text
                  style={[styles.fontName, selected && styles.fontNameSelected]}
                >
                  {t(`resume.personalization.fonts.${id}`)}
                </Text>
                <Text
                  style={[
                    styles.fontSample,
                    { fontFamily: FONT_PREVIEW[id] },
                  ]}
                  numberOfLines={1}
                >
                  {FONT_FAMILIES[id].docx} · The quick brown fox
                </Text>
              </View>
              {selected && (
                <MaterialCommunityIcons
                  name="check"
                  size={18}
                  color={palette.appliedText}
                />
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      <Text style={[styles.sectionTitle, styles.sectionGap]}>
        {t('resume.personalization.fontSize')}
      </Text>
      <View style={styles.steppers}>
        {renderStepper('title', t('resume.personalization.sizes.title'))}
        {renderStepper('subtitle', t('resume.personalization.sizes.subtitle'))}
        {renderStepper('body', t('resume.personalization.sizes.body'))}
      </View>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.sm },
    sectionTitle: {
      fontSize: 15,
      fontWeight: '700',
      color: p.textPrimary,
      marginBottom: spacing.xs,
    },
    sectionGap: { marginTop: spacing.lg },
    modeRow: { flexDirection: 'row', gap: spacing.sm },
    modeChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.pill,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    modeChipSelected: {
      backgroundColor: p.appliedBg,
      borderColor: p.appliedBg,
    },
    modeChipText: {
      color: p.textPrimary,
      fontSize: 13,
      fontWeight: '600',
    },
    modeChipTextSelected: { color: p.appliedText },
    fontList: { gap: spacing.xs },
    fontRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
      borderRadius: radius.md,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    fontRowSelected: {
      backgroundColor: p.appliedBg,
      borderColor: p.appliedBg,
    },
    fontInfo: { flex: 1 },
    fontName: { color: p.textPrimary, fontSize: 14, fontWeight: '600' },
    fontNameSelected: { color: p.appliedText, fontWeight: '700' },
    fontSample: { color: p.textSecondary, fontSize: 12, marginTop: 2 },
    steppers: { gap: spacing.xs },
    stepperRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      borderRadius: radius.md,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    stepperLabel: { color: p.textPrimary, fontSize: 14, fontWeight: '500' },
    stepperControls: { flexDirection: 'row', alignItems: 'center' },
    stepperValue: {
      minWidth: 56,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
      fontSize: 14,
      color: p.textPrimary,
      fontWeight: '600',
    },
  });

export default PersonalizationForm;
